import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Batch, LearningLanguage, Word, VerbsData } from './types';
import { fetchWords, fetchVerbs, generateSentences, translateSentences, setOnUnauthorized, logout as apiLogout, fetchSettings, saveSettings as apiSaveSettings, fetchHistory, addHistoryBatch as apiAddBatch, updateHistoryBatch as apiUpdateBatch, deleteHistoryBatch as apiDeleteBatch, fetchModelSettings } from './services/api';
import { useHistory } from './hooks/useHistory';
import { usePersistedSettings } from './hooks/usePersistedSettings';
import type { PersistedSettings } from './hooks/usePersistedSettings';
import { useTheme } from './hooks/useTheme';
import { useLanguage } from './hooks/useLanguage';
import { t } from './i18n/translations';
import type { Language } from './i18n/translations';
import type { ThemePreference } from './hooks/useTheme';
import { getBatchLearningLanguage, supportsVerbPractice } from './utils/learningLanguage';
import { buildVerbCardHref, parseVerbCardLanguage } from './utils/verbPractice';
import {
  getActiveTopicValues,
  hasActiveSavedTopic,
  isSameTopicValue,
  normalizeSavedTopics,
  normalizeTopicValue,
  softDeleteSavedTopic,
  upsertSavedTopic,
} from './utils/topics';
import CategoryGrid from './components/CategoryGrid';
import VerbColumns from './components/VerbColumns';
import GenerateSettings from './components/GenerateSettings';
import SentenceResults from './components/SentenceResults';
import WordPickerModal from './components/WordPickerModal';
import LoginPage from './components/LoginPage';
import KoniugacjaPage from './components/KoniugacjaPage';
import ModelSettingsPage from './components/ModelSettingsPage';
import ChatPage from './components/ChatPage';
import './App.css';

export default function App() {
  const [authChecked, setAuthChecked] = useState(false);
  const isSettingsPath = (p: string) => p.startsWith('/model-settings') || p.startsWith('/settings');

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showKon, setShowKon] = useState(() => window.location.pathname.startsWith('/kon'));
  const [showModelSettings, setShowModelSettings] = useState(() => isSettingsPath(window.location.pathname));
  const [showChat, setShowChat] = useState(() => window.location.pathname.startsWith('/chat'));
  const [konVerb, setKonVerb] = useState<{ group: string; verb: string } | null>(null);

  // Theme & language
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();

  // Persisted settings
  const { settings, updateSettings } = usePersistedSettings();
  const learningLanguage = settings.learningLanguage;
  const [konLearningLanguage, setKonLearningLanguage] = useState<LearningLanguage>(
    () => parseVerbCardLanguage(window.location.search) ?? settings.learningLanguage,
  );
  const verbPracticeSupported = supportsVerbPractice(learningLanguage);

  // Data
  const [, setAllWords] = useState<Word[]>([]);
  const [categoriesMap, setCategoriesMap] = useState<Record<string, Word[]>>({});
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [deselectedWords, setDeselectedWords] = useState<Set<string>>(() => new Set(settings.deselectedWords));
  const [verbMode, setVerbMode] = useState(settings.verbMode);
  const [verbDatasets, setVerbDatasets] = useState<Partial<Record<LearningLanguage, VerbsData>>>({});
  const [deselectedVerbs, setDeselectedVerbs] = useState<Set<string>>(() => new Set(settings.deselectedVerbs));
  const [categoriesCollapsed, setCategoriesCollapsed] = useState(true);
  const [modalCategory, setModalCategory] = useState<string | null>(null);

  // Generate settings
  const [sentenceCount, setSentenceCount] = useState(settings.sentenceCount);
  const [wordsPerSentence, setWordsPerSentence] = useState(settings.wordsPerSentence);
  const [tenses, setTenses] = useState(settings.tenses);
  const [grammaticalCase, setGrammaticalCase] = useState(settings.grammaticalCase);
  const [topic, setTopic] = useState(() => settings.rememberedTopic);
  const [rememberTopic, setRememberTopic] = useState(() => Boolean(settings.rememberedTopic));
  const [topicInputMode, setTopicInputMode] = useState(
    () => getActiveTopicValues(settings.savedTopics).length === 0,
  );

  // Loading states
  const [generating, setGenerating] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Mode filter for history
  const [modeFilter, setModeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Model label display preference
  const [showModelLabel, setShowModelLabel] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const verbsData = useMemo(
    () => verbDatasets[learningLanguage] || {},
    [verbDatasets, learningLanguage],
  );
  const konVerbsData = useMemo(
    () => verbDatasets[konLearningLanguage] || {},
    [verbDatasets, konLearningLanguage],
  );
  const verbPracticeAvailable = verbPracticeSupported && Object.keys(verbsData).length > 0;
  const effectiveVerbMode = verbPracticeAvailable && verbMode;
  const topicEnabled = settings.topicEnabled;
  const savedTopicValues = useMemo(
    () => getActiveTopicValues(settings.savedTopics),
    [settings.savedTopics],
  );

  // History
  const {
    sentenceHistory,
    currentBatchIndex,
    currentBatch,
    addBatch,
    deleteBatch,
    navigateBatch,
    updateTranslations,
    setHistory,
  } = useHistory();

  // Register 401 handler — show login page instead of redirecting
  useEffect(() => {
    setOnUnauthorized(() => {
      setIsLoggedIn(false);
      setShowLogin(true);
      setAuthChecked(true);
    });
  }, []);

  // Load data — fires on mount and after successful login
  useEffect(() => {
    if (showLogin) return;
    let cancelled = false;

    async function load() {
      try {
        const [words, verbsPl, verbsLt] = await Promise.all([
          fetchWords(),
          fetchVerbs('pl'),
          fetchVerbs('lt'),
        ]);
        if (cancelled) return;
        if (
          words.length === 0 &&
          Object.keys(verbsPl || {}).length === 0 &&
          Object.keys(verbsLt || {}).length === 0
        ) {
          // Empty response — likely a 401 that was handled by onUnauthorized
          return;
        }
        setAllWords(words);
        setVerbDatasets({ pl: verbsPl, lt: verbsLt });

        // Build categories map
        const cMap: Record<string, Word[]> = {};
        for (const w of words) {
          if (!cMap[w.category]) cMap[w.category] = [];
          cMap[w.category].push(w);
        }
        setCategoriesMap(cMap);

        // Select all categories by default, then apply persisted deselections
        const allCats = new Set(Object.keys(cMap));
        for (const cat of settings.deselectedCategories) {
          allCats.delete(cat);
        }
        setSelectedCategories(allCats);
        setIsLoggedIn(true);
        setAuthChecked(true);

        // Try to load settings and history from backend
        try {
          const [remoteSettings, remoteHistory, remoteModelSettings] = await Promise.all([
            fetchSettings(),
            fetchHistory(),
            fetchModelSettings(),
          ]);
          if (cancelled) return;
          if (remoteSettings) {
            // Apply remote settings if they exist
            const rs = remoteSettings as Partial<PersistedSettings>;
            const normalizedSettings: Partial<PersistedSettings> = { ...rs };
            if (
              Object.prototype.hasOwnProperty.call(rs, 'savedTopics') ||
              Object.prototype.hasOwnProperty.call(rs, 'rememberedTopic')
            ) {
              const rememberedTopic = normalizeTopicValue(rs.rememberedTopic);
              const savedTopics = rememberedTopic
                ? upsertSavedTopic(normalizeSavedTopics(rs.savedTopics), rememberedTopic)
                : normalizeSavedTopics(rs.savedTopics);
              normalizedSettings.savedTopics = savedTopics;
              normalizedSettings.rememberedTopic = rememberedTopic;
              if (typeof rs.topicEnabled !== 'boolean') {
                normalizedSettings.topicEnabled = Boolean(rememberedTopic || getActiveTopicValues(savedTopics).length > 0);
              }
              setTopic(rememberedTopic);
              setRememberTopic(Boolean(rememberedTopic));
              setTopicInputMode(getActiveTopicValues(savedTopics).length === 0);
            }
            updateSettings(normalizedSettings);
            if (rs.verbMode !== undefined) setVerbMode(rs.verbMode);
            if (rs.deselectedWords) setDeselectedWords(new Set(rs.deselectedWords));
            if (rs.deselectedVerbs) setDeselectedVerbs(new Set(rs.deselectedVerbs));
            if (rs.sentenceCount) setSentenceCount(rs.sentenceCount);
            if (rs.wordsPerSentence) setWordsPerSentence(rs.wordsPerSentence);
            if (rs.tenses) setTenses(rs.tenses);
            if (typeof rs.grammaticalCase === 'string') setGrammaticalCase(rs.grammaticalCase);
            if (rs.deselectedCategories) {
              const cats = new Set(Object.keys(cMap));
              for (const cat of rs.deselectedCategories) {
                cats.delete(cat);
              }
              setSelectedCategories(cats);
            }
          }
          if (remoteHistory !== null) {
            setHistory(remoteHistory);
          }
          if (remoteModelSettings?.modelSettings?.showModelLabel !== undefined) {
            setShowModelLabel(remoteModelSettings.modelSettings.showModelLabel);
          }
          if (remoteModelSettings?.modelSettings?.showDebugInfo !== undefined) {
            setShowDebugInfo(remoteModelSettings.modelSettings.showDebugInfo);
          }
        } catch {
          // Backend settings/history not available — use local
        }
      } catch {
        if (!cancelled) setLoadError(true);
      }
    }

    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLogin]);

  // Sync settings to backend (debounced via usePersistedSettings)
  useEffect(() => {
    if (!isLoggedIn) return;
    const timer = setTimeout(() => {
      apiSaveSettings(settings).catch(() => {/* ignore */});
    }, 1100);
    return () => clearTimeout(timer);
  }, [settings, isLoggedIn]);

  // Handle browser back/forward for koniugacja page
  useEffect(() => {
    if (!showKon) {
      setKonLearningLanguage(learningLanguage);
    }
  }, [learningLanguage, showKon]);

  useEffect(() => {
    const handlePopState = () => {
      setShowKon(window.location.pathname.startsWith('/kon'));
      setShowModelSettings(isSettingsPath(window.location.pathname));
      setShowChat(window.location.pathname.startsWith('/chat'));
      setKonLearningLanguage(parseVerbCardLanguage(window.location.search) ?? learningLanguage);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [learningLanguage]);

  useEffect(() => {
    if (savedTopicValues.length === 0) {
      setTopicInputMode(true);
    }
  }, [savedTopicValues.length]);

  useEffect(() => {
    if (verbPracticeSupported) return;

    if (verbMode) {
      setVerbMode(false);
      updateSettings({ verbMode: false });
    }

    if (showKon) {
      setShowKon(false);
      setKonVerb(null);
      if (window.location.pathname.startsWith('/kon')) {
        window.history.replaceState({}, '', '/');
      }
    }
  }, [showKon, updateSettings, verbMode, verbPracticeSupported]);

  const batchMatchesHistoryFilters = useCallback((
    batch: Batch,
    nextModeFilter: string = modeFilter,
    nextCategoryFilter: string = categoryFilter,
  ) => {
    if (getBatchLearningLanguage(batch) !== learningLanguage) return false;
    if (nextModeFilter !== 'all' && batch.mode !== nextModeFilter) return false;
    if (nextCategoryFilter !== 'all' && batch.category !== nextCategoryFilter) return false;
    return true;
  }, [categoryFilter, learningLanguage, modeFilter]);

  const findLatestMatchingBatchIndex = useCallback((
    nextModeFilter: string = modeFilter,
    nextCategoryFilter: string = categoryFilter,
  ) => {
    for (let i = sentenceHistory.length - 1; i >= 0; i--) {
      if (batchMatchesHistoryFilters(sentenceHistory[i], nextModeFilter, nextCategoryFilter)) {
        return i;
      }
    }
    return null;
  }, [batchMatchesHistoryFilters, categoryFilter, modeFilter, sentenceHistory]);

  const languageBatchIndices = useMemo(() => {
    const indices: number[] = [];
    for (let i = 0; i < sentenceHistory.length; i++) {
      if (getBatchLearningLanguage(sentenceHistory[i]) === learningLanguage) {
        indices.push(i);
      }
    }
    return indices;
  }, [sentenceHistory, learningLanguage]);

  // Available categories for filtering (only categories that have been used for generation)
  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    for (const index of languageBatchIndices) {
      const batch = sentenceHistory[index];
      if (batch.category) cats.add(batch.category);
    }
    return [...cats].sort();
  }, [languageBatchIndices, sentenceHistory]);

  // Filtered batch indices
  const filteredBatchIndices = useMemo(() => {
    const hasModeFilter = modeFilter !== 'all';
    const hasCategoryFilter = categoryFilter !== 'all';
    if (!hasModeFilter && !hasCategoryFilter) return languageBatchIndices;
    const indices: number[] = [];
    for (const i of languageBatchIndices) {
      const b = sentenceHistory[i];
      if (hasModeFilter && b.mode !== modeFilter) continue;
      if (hasCategoryFilter && b.category !== categoryFilter) continue;
      indices.push(i);
    }
    return indices;
  }, [sentenceHistory, languageBatchIndices, modeFilter, categoryFilter]);

  useEffect(() => {
    if (categoryFilter === 'all') return;
    if (availableCategories.includes(categoryFilter)) return;
    setCategoryFilter('all');
  }, [availableCategories, categoryFilter]);

  useEffect(() => {
    if (filteredBatchIndices.length === 0) return;
    if (filteredBatchIndices.includes(currentBatchIndex)) return;
    const targetRealIndex = filteredBatchIndices[filteredBatchIndices.length - 1];
    navigateBatch(targetRealIndex - currentBatchIndex);
  }, [filteredBatchIndices, currentBatchIndex, navigateBatch]);

  // Filtered navigation
  const handleFilteredNavigate = useCallback((direction: number) => {
    if (filteredBatchIndices.length === 0) {
      return;
    }
    const currentFilteredPos = filteredBatchIndices.indexOf(currentBatchIndex);
    if (currentFilteredPos === -1) {
      const fallbackRealIndex = direction > 0
        ? filteredBatchIndices[0]
        : filteredBatchIndices[filteredBatchIndices.length - 1];
      navigateBatch(fallbackRealIndex - currentBatchIndex);
      return;
    }
    const nextFilteredPos = currentFilteredPos + direction;
    if (nextFilteredPos >= 0 && nextFilteredPos < filteredBatchIndices.length) {
      const targetRealIndex = filteredBatchIndices[nextFilteredPos];
      // Navigate to the absolute index
      navigateBatch(targetRealIndex - currentBatchIndex);
    }
  }, [filteredBatchIndices, currentBatchIndex, navigateBatch]);

  // When filter changes, navigate to the nearest matching batch
  const handleModeFilterChange = useCallback((mode: string) => {
    setModeFilter(mode);
    const nextCategoryFilter = mode !== 'main' && mode !== 'W' ? 'all' : categoryFilter;
    if (nextCategoryFilter !== categoryFilter) {
      setCategoryFilter(nextCategoryFilter);
    }
    const targetIndex = findLatestMatchingBatchIndex(mode, nextCategoryFilter);
    if (targetIndex !== null) {
      navigateBatch(targetIndex - currentBatchIndex);
    }
  }, [categoryFilter, currentBatchIndex, findLatestMatchingBatchIndex, navigateBatch]);

  // When category filter changes, navigate to the nearest matching batch
  const handleCategoryFilterChange = useCallback((cat: string) => {
    setCategoryFilter(cat);
    const targetIndex = findLatestMatchingBatchIndex(modeFilter, cat);
    if (targetIndex !== null) {
      navigateBatch(targetIndex - currentBatchIndex);
    }
  }, [currentBatchIndex, findLatestMatchingBatchIndex, modeFilter, navigateBatch]);

  // Get the batch to display (respecting filter)
  const displayBatch = useMemo(() => {
    if (filteredBatchIndices.includes(currentBatchIndex)) return currentBatch;
    // Current batch doesn't match filter — show nothing
    return null;
  }, [filteredBatchIndices, currentBatchIndex, currentBatch]);

  // Computed values
  const getSelectedWords = useCallback((): Word[] => {
    const words: Word[] = [];
    for (const cat of selectedCategories) {
      if (!categoriesMap[cat]) continue;
      for (const w of categoriesMap[cat]) {
        const key = `${cat}::${w.pl}`;
        if (!deselectedWords.has(key)) {
          words.push(w);
        }
      }
    }
    return words;
  }, [selectedCategories, categoriesMap, deselectedWords]);

  const getSelectedVerbs = useCallback(() => {
    const verbs: typeof verbsData[string] = [];
    for (const group of Object.keys(verbsData)) {
      for (const v of verbsData[group]) {
        const key = `${group}::${v.inf}`;
        if (!deselectedVerbs.has(key)) {
          verbs.push(v);
        }
      }
    }
    return verbs;
  }, [verbsData, deselectedVerbs]);

  const wordCount = effectiveVerbMode ? getSelectedVerbs().length : getSelectedWords().length;
  const wordCountLabel = effectiveVerbMode ? `${wordCount} ${t(language, 'count.verbs')}` : `${wordCount} ${t(language, 'count.words')}`;

  // Handlers
  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      const allCats = Object.keys(categoriesMap);
      updateSettings({ deselectedCategories: allCats.filter((c) => !next.has(c)) });
      return next;
    });
  };

  const selectAllCategories = () => {
    setSelectedCategories(new Set(Object.keys(categoriesMap)));
    setDeselectedWords(new Set());
    updateSettings({ deselectedCategories: [], deselectedWords: [] });
  };

  const deselectAllCategories = () => {
    setSelectedCategories(new Set());
    updateSettings({ deselectedCategories: Object.keys(categoriesMap) });
  };

  const toggleCategoriesCollapsed = () => {
    setCategoriesCollapsed((prev) => !prev);
  };

  const openModal = (cat: string) => {
    setModalCategory(cat);
  };

  const closeModal = () => {
    setModalCategory(null);
  };

  const toggleWord = (key: string) => {
    setDeselectedWords((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      updateSettings({ deselectedWords: [...next] });
      return next;
    });
  };

  const modalSelectAll = () => {
    if (!modalCategory || !categoriesMap[modalCategory]) return;
    setDeselectedWords((prev) => {
      const next = new Set(prev);
      for (const w of categoriesMap[modalCategory]) {
        next.delete(`${modalCategory}::${w.pl}`);
      }
      updateSettings({ deselectedWords: [...next] });
      return next;
    });
  };

  const modalDeselectAll = () => {
    if (!modalCategory || !categoriesMap[modalCategory]) return;
    setDeselectedWords((prev) => {
      const next = new Set(prev);
      for (const w of categoriesMap[modalCategory]) {
        next.add(`${modalCategory}::${w.pl}`);
      }
      updateSettings({ deselectedWords: [...next] });
      return next;
    });
  };

  const toggleVerb = (key: string) => {
    setDeselectedVerbs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      updateSettings({ deselectedVerbs: [...next] });
      return next;
    });
  };

  const toggleVerbGroup = (group: string) => {
    if (!verbsData[group]) return;
    const verbs = verbsData[group];
    let allSelected = true;
    for (const v of verbs) {
      if (deselectedVerbs.has(`${group}::${v.inf}`)) { allSelected = false; break; }
    }
    setDeselectedVerbs((prev) => {
      const next = new Set(prev);
      for (const v of verbs) {
        const key = `${group}::${v.inf}`;
        if (allSelected) next.add(key);
        else next.delete(key);
      }
      updateSettings({ deselectedVerbs: [...next] });
      return next;
    });
  };

  const selectVerbGroup = (group: string) => {
    if (!verbsData[group]) return;
    setDeselectedVerbs((prev) => {
      const next = new Set(prev);
      for (const v of verbsData[group]) {
        next.delete(`${group}::${v.inf}`);
      }
      updateSettings({ deselectedVerbs: [...next] });
      return next;
    });
  };

  const deselectVerbGroup = (group: string) => {
    if (!verbsData[group]) return;
    setDeselectedVerbs((prev) => {
      const next = new Set(prev);
      for (const v of verbsData[group]) {
        next.add(`${group}::${v.inf}`);
      }
      updateSettings({ deselectedVerbs: [...next] });
      return next;
    });
  };

  const getSelectedTenses = (): string[] => {
    const t: string[] = [];
    if (tenses.present) t.push('present');
    if (tenses.past) t.push('past');
    if (tenses.future) t.push('future');
    return t.length > 0 ? t : ['present'];
  };

  const resetFilters = useCallback(() => {
    setModeFilter('all');
    setCategoryFilter('all');
  }, []);

  const handleTopicChange = useCallback((value: string) => {
    setTopic(value);
    if (!topicInputMode) {
      const normalizedTopic = normalizeTopicValue(value);
      setRememberTopic(Boolean(normalizedTopic) && isSameTopicValue(normalizedTopic, settings.rememberedTopic));
    }
  }, [settings.rememberedTopic, topicInputMode]);

  const handleCreateNewTopic = useCallback(() => {
    setTopicInputMode(true);
    setTopic('');
    setRememberTopic(false);
  }, []);

  const handleUseSavedTopics = useCallback(() => {
    const rememberedTopic = normalizeTopicValue(settings.rememberedTopic);
    const nextTopic = hasActiveSavedTopic(settings.savedTopics, topic) ? normalizeTopicValue(topic) : rememberedTopic;
    setTopicInputMode(false);
    setTopic(nextTopic);
    setRememberTopic(Boolean(nextTopic) && isSameTopicValue(nextTopic, rememberedTopic));
  }, [settings.rememberedTopic, settings.savedTopics, topic]);

  const handleDeleteTopic = useCallback(() => {
    const normalizedTopic = normalizeTopicValue(topic);
    if (!normalizedTopic || !hasActiveSavedTopic(settings.savedTopics, normalizedTopic)) return;
    if (!window.confirm(t(language, 'topic.deleteConfirm'))) return;

    const nextSavedTopics = softDeleteSavedTopic(settings.savedTopics, normalizedTopic);
    const nextRememberedTopic = isSameTopicValue(settings.rememberedTopic, normalizedTopic)
      ? ''
      : normalizeTopicValue(settings.rememberedTopic);

    updateSettings({
      savedTopics: nextSavedTopics,
      rememberedTopic: nextRememberedTopic,
    });

    setTopic('');
    setRememberTopic(false);
    setTopicInputMode(getActiveTopicValues(nextSavedTopics).length === 0);
  }, [language, settings.rememberedTopic, settings.savedTopics, topic, updateSettings]);

  const handleGenerationTopicSuccess = useCallback((usedTopic: string) => {
    const normalizedTopic = normalizeTopicValue(usedTopic);
    const nextSavedTopics = normalizedTopic
      ? upsertSavedTopic(settings.savedTopics, normalizedTopic)
      : settings.savedTopics;
    const previousRememberedTopic = normalizeTopicValue(settings.rememberedTopic);
    const shouldUpdateRememberedTopic = rememberTopic;
    const shouldClearRememberedTopic = !shouldUpdateRememberedTopic
      && Boolean(normalizedTopic)
      && isSameTopicValue(previousRememberedTopic, normalizedTopic);
    const nextRememberedTopic = shouldUpdateRememberedTopic
      ? normalizedTopic
      : shouldClearRememberedTopic
        ? ''
        : previousRememberedTopic;

    if (normalizedTopic || shouldUpdateRememberedTopic || shouldClearRememberedTopic) {
      updateSettings({
        ...(normalizedTopic ? { savedTopics: nextSavedTopics } : {}),
        ...((shouldUpdateRememberedTopic || shouldClearRememberedTopic)
          ? { rememberedTopic: nextRememberedTopic }
          : {}),
      });
    }

    if (normalizedTopic) {
      setTopic(shouldUpdateRememberedTopic ? normalizedTopic : '');
      setTopicInputMode(getActiveTopicValues(nextSavedTopics).length === 0);
    }
    setRememberTopic(Boolean(nextRememberedTopic) && isSameTopicValue(normalizedTopic, nextRememberedTopic));
  }, [rememberTopic, settings.rememberedTopic, settings.savedTopics, updateSettings]);

  const runGeneration = useCallback(async (
    payload: object,
    mode: string,
    batchLearningLanguage: LearningLanguage,
    category?: string,
  ): Promise<boolean> => {
    const normalizedTopic = topicEnabled ? normalizeTopicValue(topic) : '';
    const normalizedCase = grammaticalCase.trim();
    const generationPayload = {
      ...payload,
      ...(normalizedTopic ? { topic: normalizedTopic } : {}),
      ...(normalizedCase ? { grammaticalCase: normalizedCase } : {}),
    };

    resetFilters();
    setGenerating(true);
    try {
      const data = await generateSentences(generationPayload);
      if (data.sentences && data.sentences.length > 0) {
        const batch = addBatch(
          data.sentences,
          data.usedWords || [],
          mode,
          data.llmRequest,
          data.tokenUsage,
          category,
          batchLearningLanguage,
          normalizedTopic || undefined,
          normalizedCase || undefined,
        );
        apiAddBatch(batch).catch(() => {/* ignore */});
        handleGenerationTopicSuccess(normalizedTopic);
        return true;
      }
      return false;
    } catch (err) {
      alert(t(language, 'error.generation') + (err instanceof Error ? err.message : String(err)));
      return false;
    } finally {
      setGenerating(false);
    }
  }, [addBatch, grammaticalCase, handleGenerationTopicSuccess, language, resetFilters, topic, topicEnabled]);

  const handleGenerate = async () => {
    const count = parseInt(sentenceCount, 10);
    const tenseList = getSelectedTenses();
    let payload: object;
    let category: string | undefined;

    if (effectiveVerbMode) {
      const verbs = getSelectedVerbs();
      if (verbs.length === 0) return;
      payload = { mode: 'verbs', verbs, sentenceCount: count, wordsPerSentence, tenses: tenseList, learningLanguage };
    } else {
      const words = getSelectedWords();
      if (words.length === 0) return;
      // If only one category is selected, store it
      if (selectedCategories.size === 1) {
        category = [...selectedCategories][0];
      }
      payload = { words, sentenceCount: count, wordsPerSentence, tenses: tenseList, learningLanguage };
    }

    await runGeneration(payload, effectiveVerbMode ? 'K' : 'main', learningLanguage, category);
  };

  const handleGenerateSingle = async (
    verbInf: string,
    verbLearningLanguage: LearningLanguage = learningLanguage,
  ) => {
    if (!supportsVerbPractice(verbLearningLanguage)) return;
    const count = parseInt(sentenceCount, 10);
    const tenseList = getSelectedTenses();
    const selectedVerbData = verbDatasets[verbLearningLanguage] || {};
    let verb = null;
    for (const group of Object.keys(selectedVerbData)) {
      for (const v of selectedVerbData[group]) {
        if (v.inf === verbInf) { verb = v; break; }
      }
      if (verb) break;
    }
    if (!verb) return;

    const payload = { mode: 'verbs', verbs: [verb], sentenceCount: count, wordsPerSentence, tenses: tenseList, learningLanguage: verbLearningLanguage };
    await runGeneration(payload, 'V', verbLearningLanguage);
  };

  const handleGenerateForWord = async (
    word: Word,
    wordLearningLanguage: LearningLanguage = learningLanguage,
  ) => {
    const count = parseInt(sentenceCount, 10);
    const tenseList = getSelectedTenses();
    const payload = {
      words: [word],
      sentenceCount: count,
      wordsPerSentence,
      tenses: tenseList,
      learningLanguage: wordLearningLanguage,
    };
    await runGeneration(payload, 'W', wordLearningLanguage, word.category);
  };

  const handleGenerateForCategory = async (category: string) => {
    if (!categoriesMap[category]) return;
    const words: Word[] = [];
    for (const w of categoriesMap[category]) {
      const key = `${category}::${w.pl}`;
      if (!deselectedWords.has(key)) words.push(w);
    }
    if (words.length === 0) return;
    const count = parseInt(sentenceCount, 10);
    const tenseList = getSelectedTenses();
    const payload = { words, sentenceCount: count, wordsPerSentence, tenses: tenseList, learningLanguage };
    setModalCategory(null);
    setCategoriesCollapsed(true);
    try {
      await runGeneration(payload, 'main', learningLanguage, category);
    } finally {
      setTimeout(() => {
        document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleFilterByCategory = (category: string) => {
    setModalCategory(null);
    setCategoriesCollapsed(true);
    setModeFilter('main');
    setCategoryFilter(category);
    const targetIndex = findLatestMatchingBatchIndex('main', category);
    if (targetIndex !== null) {
      navigateBatch(targetIndex - currentBatchIndex);
      setTimeout(() => {
        document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleTranslate = async () => {
    if (!currentBatch) return;
    setTranslating(true);
    try {
      const result = await translateSentences(currentBatch.sentences, getBatchLearningLanguage(currentBatch));
      if (result.translations.length > 0) {
        updateTranslations(result.translations);
        apiUpdateBatch(currentBatch.timestamp, { translations: result.translations }).catch(() => {/* ignore */});
      }
    } catch (err) {
      alert(t(language, 'error.translation') + (err instanceof Error ? err.message : String(err)));
    } finally {
      setTranslating(false);
    }
  };

  const handleDelete = useCallback(() => {
    if (!currentBatch) return;
    const ts = currentBatch.timestamp;
    deleteBatch();
    apiDeleteBatch(ts).catch(() => {/* ignore */});
  }, [currentBatch, deleteBatch]);

  const handleDisplayPreferencesChange = useCallback((displayPreferences: {
    showModelLabel: boolean;
    showDebugInfo: boolean;
  }) => {
    setShowModelLabel(displayPreferences.showModelLabel);
    setShowDebugInfo(displayPreferences.showDebugInfo);
  }, []);

  const handleSentenceCountChange = useCallback((v: string) => {
    setSentenceCount(v);
    updateSettings({ sentenceCount: v });
  }, [updateSettings]);

  const handleWordsPerSentenceChange = useCallback((v: string) => {
    setWordsPerSentence(v);
    updateSettings({ wordsPerSentence: v });
  }, [updateSettings]);

  const handleTenseChange = useCallback((tense: 'present' | 'past' | 'future', checked: boolean) => {
    setTenses((prev) => {
      const next = { ...prev, [tense]: checked };
      updateSettings({ tenses: next });
      return next;
    });
  }, [updateSettings]);

  const handleGrammaticalCaseChange = useCallback((value: string) => {
    setGrammaticalCase(value);
    updateSettings({ grammaticalCase: value });
  }, [updateSettings]);

  const openKonPage = useCallback((
    verbLanguage: LearningLanguage,
    initialVerb?: { group: string; verb: string } | null,
  ) => {
    if (!supportsVerbPractice(verbLanguage)) return;
    setKonLearningLanguage(verbLanguage);
    setKonVerb(initialVerb ?? null);
    setShowKon(true);
    window.history.pushState({}, '', buildVerbCardHref(initialVerb?.group, initialVerb?.verb, verbLanguage));
  }, []);

  const handleNavigateToVerb = useCallback((
    group: string,
    verbInf: string,
    verbLanguage: LearningLanguage = learningLanguage,
  ) => {
    if (!supportsVerbPractice(verbLanguage)) return;
    openKonPage(verbLanguage, { group, verb: verbInf });
  }, [learningLanguage, openKonPage]);

  const handleLogout = useCallback(() => {
    if (!window.confirm(t(language, 'btn.logoutConfirm'))) return;
    apiLogout().catch(() => {/* ignore */});
    setIsLoggedIn(false);
    setShowLogin(true);
  }, [language]);

  // Show loading state while checking auth
  if (!authChecked) {
    return <div aria-busy="true" aria-label="Loading" />;
  }

  // Login page
  if (showLogin || !isLoggedIn) {
    return (
      <LoginPage
        onLoginSuccess={() => {
          setIsLoggedIn(true);
          setShowLogin(false);
        }}
        language={language}
      />
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ color: 'var(--danger)' }}>{t(language, 'error.loadDict')}</p>
      </div>
    );
  }

  // Model Settings page
  if (showModelSettings) {
    return (
      <ModelSettingsPage
        onBack={() => {
          setShowModelSettings(false);
          window.history.pushState({}, '', '/');
        }}
        onNavigateToChat={() => {
          setShowModelSettings(false);
          setShowChat(true);
          window.history.pushState({}, '', '/chat');
        }}
        onNavigateToKon={verbPracticeAvailable ? () => {
          setShowModelSettings(false);
          openKonPage(learningLanguage);
        } : undefined}
        learningLanguage={learningLanguage}
        onLearningLanguageChange={(nextLanguage) => updateSettings({ learningLanguage: nextLanguage })}
        topicEnabled={topicEnabled}
        onTopicEnabledChange={(enabled) => updateSettings({ topicEnabled: enabled })}
        onDisplayPreferencesChange={handleDisplayPreferencesChange}
        language={language}
      />
    );
  }

  // Chat page
  if (showChat) {
    return (
      <ChatPage
        onBack={() => {
          setShowChat(false);
          window.history.pushState({}, '', '/');
        }}
        onNavigateToModelSettings={() => {
          setShowChat(false);
          setShowModelSettings(true);
          window.history.pushState({}, '', '/settings');
        }}
        onNavigateToKon={verbPracticeAvailable ? () => {
          setShowChat(false);
          openKonPage(learningLanguage);
        } : undefined}
        showDebugInfo={showDebugInfo}
        language={language}
      />
    );
  }

  // Koniugacja page
  if (showKon && supportsVerbPractice(konLearningLanguage)) {
    return (
      <KoniugacjaPage
        verbsData={konVerbsData}
        initialVerb={konVerb ?? undefined}
        onBack={() => {
          setShowKon(false);
          setKonVerb(null);
          window.history.pushState({}, '', '/');
        }}
        onGenerateSingle={(verbInf: string) => {
          setShowKon(false);
          setKonVerb(null);
          window.history.pushState({}, '', '/');
          handleGenerateSingle(verbInf, konLearningLanguage);
        }}
        onNavigateToSettings={() => {
          setShowKon(false);
          setKonVerb(null);
          setShowModelSettings(true);
          window.history.pushState({}, '', '/settings');
        }}
        onNavigateToChat={() => {
          setShowKon(false);
          setKonVerb(null);
          setShowChat(true);
          window.history.pushState({}, '', '/chat');
        }}
        learningLanguage={konLearningLanguage}
        language={language}
      />
    );
  }

  return (
    <>
      <header>
        <div className="header-top">
          <h1>{t(language, 'app.title')}</h1>
          <nav className="nav-links">
            {verbPracticeAvailable && (
              <a
                href={buildVerbCardHref(undefined, undefined, learningLanguage)}
                onClick={(e) => {
                  e.preventDefault();
                  openKonPage(learningLanguage);
                }}
              >
                {t(language, 'nav.koniugacja')}
              </a>
            )}
            <a
              href="/settings"
              className="nav-settings-link"
              onClick={(e) => {
                e.preventDefault();
                setShowModelSettings(true);
                window.history.pushState({}, '', '/settings');
              }}
            >
              {t(language, 'nav.settings')}
            </a>
            <a
              href="/chat"
              onClick={(e) => {
                e.preventDefault();
                setShowChat(true);
                window.history.pushState({}, '', '/chat');
              }}
            >
              {t(language, 'nav.chat')}
            </a>
          </nav>
          <div className="header-toolbar">
            <div className="theme-switcher">
              {(['light', 'auto', 'dark'] as ThemePreference[]).map((opt) => (
                <button
                  key={opt}
                  className={theme === opt ? 'active' : ''}
                  onClick={() => setTheme(opt)}
                  title={opt}
                >
                  {opt === 'light' ? '☀️' : opt === 'dark' ? '🌙' : t(language, 'theme.auto')}
                </button>
              ))}
            </div>
            <div className="lang-switcher">
              {(['en', 'ru', 'pl'] as Language[]).map((lng) => (
                <button
                  key={lng}
                  className={language === lng ? 'active' : ''}
                  onClick={() => setLanguage(lng)}
                >
                  {lng.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              className="btn-logout"
              onClick={handleLogout}
              title={t(language, 'btn.logout')}
            >
              {t(language, 'btn.logout')}
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Categories Section */}
        <section id="categories-section">
          <div className="section-header">
            <div className="section-header-row">
              <div className="mode-switcher">
                {verbPracticeAvailable && (
                  <label className="switch-label">
                    <input
                      type="checkbox"
                      id="verb-mode-toggle"
                      checked={verbMode}
                      onChange={(e) => {
                        setVerbMode(e.target.checked);
                        updateSettings({ verbMode: e.target.checked });
                      }}
                    />
                    <span className="switch-track">
                      <span className="switch-thumb"></span>
                    </span>
                    <span className="switch-text">{t(language, 'switch.verbs')}</span>
                  </label>
                )}
              </div>
              <h2>
                <span id="section-title">{effectiveVerbMode ? t(language, 'section.conjugations') : t(language, 'section.categories')}</span>
              </h2>
              <span id="word-count" className="badge">
                {wordCountLabel}
              </span>
              <button
                id="toggle-cats"
                className="btn btn-sm btn-outline"
                title={categoriesCollapsed ? t(language, 'btn.show') : t(language, 'btn.hide')}
                onClick={toggleCategoriesCollapsed}
              >
                {categoriesCollapsed ? t(language, 'btn.show') : t(language, 'btn.hide')}
              </button>
            </div>
            {!effectiveVerbMode && !categoriesCollapsed && (
              <div className="section-actions">
                <button
                  id="select-all-cats"
                  className="btn btn-sm"
                  onClick={selectAllCategories}
                >
                  {t(language, 'btn.selectAll')}
                </button>
                <button
                  id="deselect-all-cats"
                  className="btn btn-sm btn-outline"
                  onClick={deselectAllCategories}
                >
                  {t(language, 'btn.deselectAll')}
                </button>
              </div>
            )}
          </div>

          <CategoryGrid
            categoriesMap={categoriesMap}
            selectedCategories={selectedCategories}
            deselectedWords={deselectedWords}
            collapsed={categoriesCollapsed}
            verbMode={effectiveVerbMode}
            onToggleCategory={toggleCategory}
            onOpenModal={openModal}
            language={language}
          />

          <VerbColumns
            verbsData={verbsData}
            deselectedVerbs={deselectedVerbs}
            collapsed={categoriesCollapsed}
            verbMode={verbPracticeAvailable && verbMode}
            onToggleVerb={toggleVerb}
            onToggleVerbGroup={toggleVerbGroup}
            onSelectVerbGroup={selectVerbGroup}
            onDeselectVerbGroup={deselectVerbGroup}
            learningLanguage={learningLanguage}
            language={language}
          />
        </section>

        {/* Generate Section */}
        <section id="generate-section">
          <div className="generate-bar">
            <GenerateSettings
              sentenceCount={sentenceCount}
              wordsPerSentence={wordsPerSentence}
              tenses={tenses}
              grammaticalCase={grammaticalCase}
              learningLanguage={learningLanguage}
              topicEnabled={topicEnabled}
              topic={topic}
              rememberTopic={rememberTopic}
              topicInputMode={topicInputMode}
              savedTopics={savedTopicValues}
              onSentenceCountChange={handleSentenceCountChange}
              onWordsPerSentenceChange={handleWordsPerSentenceChange}
              onTenseChange={handleTenseChange}
              onGrammaticalCaseChange={handleGrammaticalCaseChange}
              onTopicChange={handleTopicChange}
              onRememberTopicChange={setRememberTopic}
              onCreateNewTopic={handleCreateNewTopic}
              onUseSavedTopics={handleUseSavedTopics}
              onDeleteTopic={handleDeleteTopic}
              language={language}
            />
            <button
              id="generate-btn"
              className="btn btn-primary"
              disabled={wordCount === 0 || generating}
              onClick={handleGenerate}
            >
              {generating ? (
                <span className="btn-content"><span className="spinner spinner-inline"></span> {t(language, 'loading.generating')}</span>
              ) : (
                t(language, 'btn.generate')
              )}
            </button>
          </div>
        </section>

        {/* Results Section */}
        <SentenceResults
          batch={displayBatch}
          currentBatchIndex={currentBatchIndex}
          totalBatches={languageBatchIndices.length}
          translating={translating}
          verbsDataByLanguage={verbDatasets}
          categoriesMap={categoriesMap}
          onTranslate={handleTranslate}
          onNavigate={handleFilteredNavigate}
          onDelete={handleDelete}
          generating={generating}
          onGenerateSingle={handleGenerateSingle}
          onGenerateForWord={handleGenerateForWord}
          onNavigateToVerb={handleNavigateToVerb}
          filteredBatchIndices={filteredBatchIndices}
          modeFilter={modeFilter}
          onModeFilterChange={handleModeFilterChange}
          categoryFilter={categoryFilter}
          availableCategories={availableCategories}
          onCategoryFilterChange={handleCategoryFilterChange}
          showModelLabel={showModelLabel}
          showDebugInfo={showDebugInfo}
          language={language}
        />
      </main>

      {/* Word Picker Modal */}
      <WordPickerModal
        category={modalCategory}
        words={modalCategory && categoriesMap[modalCategory] ? categoriesMap[modalCategory] : []}
        deselectedWords={deselectedWords}
        onToggleWord={toggleWord}
        onSelectAll={modalSelectAll}
        onDeselectAll={modalDeselectAll}
        onClose={closeModal}
        onGenerateCategory={handleGenerateForCategory}
        onFilterByCategory={handleFilterByCategory}
        generating={generating}
        learningLanguage={learningLanguage}
        language={language}
      />
    </>
  );
}
